import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import injuriesData from '../../data/injuries.json';
import gaHero from '../../assets/images/heroes/ga-hero-main.webp';
import ncHero from '../../assets/images/heroes/nc-hero-main.webp';
import './InjuryHub.css';

const BRAND_HEROES = { gso: gaHero, 'apex-nc': ncHero, 'apex-tn': ncHero };

export default function InjuryHub() {
  const { injurySlug } = useParams();
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandName = brand?.name || state.client?.name || 'Our Practice';
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const brandKey = brand?.brandKey || 'gso';

  const injury = injuriesData.find(i => i.slug === injurySlug);

  if (!injury) {
    return (
      <div className="injury-hub-page">
        <div className="container" style={{ padding: '80px 20px', textAlign: 'center' }}>
          <p>{t('blog.notFound', locale)}</p>
        </div>
      </div>
    );
  }

  const content = injury.translations[locale] || injury.translations.en;

  const canonicalUrl = `https://${baseDomain}/injuries/${injurySlug}`;
  const pageTitle = `${content.title} Treatment | ${brandName}`;
  const pageDesc = content.desc;
  const ogImage = `https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: pageTitle,
    description: pageDesc,
    url: canonicalUrl,
    about: {
      '@type': 'MedicalCondition',
      name: content.title,
      description: content.desc,
      signOrSymptom: (content.symptoms || []).map(s => ({
        '@type': 'MedicalSymptom',
        name: s,
      })),
      possibleTreatment: (content.treatments || []).map(tr => ({
        '@type': 'MedicalTherapy',
        name: tr,
      })),
    },
  };

  const aboutLabel = t('injury.aboutTreatment', locale).replace('{{injury}}', content.title);
  const locationCount = brand?.locations?.length;

  return (
    <div className="injury-hub-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/injuries/${injurySlug}`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      {/* Hero with background image + glass overlay */}
      <section
        className="injury-hero"
        style={{ backgroundImage: `url(${BRAND_HEROES[brandKey] || gaHero})` }}
      >
        <div className="injury-hero-overlay">
          <div className="injury-content container">
            <div className="injury-badge">{t('injury.badge', locale)}</div>
            <h1>{content.title}</h1>
            <p className="injury-hero-desc">{content.desc}</p>
            <a
              href={localizedPath('/request-appt', locale)}
              className="injury-hero-cta"
            >
              {t('injury.scheduleCta', locale)}
            </a>
          </div>
        </div>
      </section>

      {/* Main content */}
      <div className="injury-content container injury-main">

        {/* About */}
        <section className="injury-about">
          <h2>{aboutLabel}</h2>
          <p className="injury-about-desc">{t('injury.aboutDesc', locale)}</p>
        </section>

        {/* Symptoms */}
        <section className="injury-section">
          <h2>{t('injury.symptoms', locale)}</h2>
          <ul className="injury-list">
            {(content.symptoms || []).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </section>

        {/* Causes */}
        <section className="injury-section">
          <h2>{t('injury.causes', locale)}</h2>
          <ul className="injury-list">
            {(content.causes || []).map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </section>

        {/* Treatments */}
        <section className="injury-section">
          <h2>{t('injury.treatments', locale)}</h2>
          <ul className="injury-list">
            {(content.treatments || []).map((tr, i) => (
              <li key={i}>{tr}</li>
            ))}
          </ul>
        </section>

        {/* Locations */}
        {locationCount > 0 && (
        <section className="injury-locations">
          <p>{t('injury.availableLocations', locale).replace('{{count}}', String(locationCount))}</p>
          <a
            href={localizedPath('/locations', locale)}
            className="injury-locations-cta"
          >
            {t('injury.locationsCta', locale)}
          </a>
        </section>
        )}

      </div>

      {/* Bottom CTA */}
      <section className="injury-cta">
        <div className="container">
          <h2>{t('injury.ctaHeading', locale)}</h2>
          <a
            href={localizedPath('/request-appt', locale)}
            className="injury-cta-btn"
          >
            {t('injury.scheduleCta', locale)}
          </a>
        </div>
      </section>
    </div>
  );
}
